import React, { useState, useEffect, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from 'primereact/button';
import { FileUpload } from "primereact/fileupload";
import { RadioButton } from "primereact/radiobutton";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/axios";
import { Skeleton } from "primereact/skeleton";
import { Sidebar } from "primereact/sidebar";
import { Checkbox } from "primereact/checkbox";
import nouser from "../../assets/images/no_user.png";

const Profile = () => {
  const { userId, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);

  /* ---------------- Account ---------------- */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  /* ---------------- Personal ---------------- */
  const [phone, setPhone] = useState("");

  /* ---------------- Addresses ---------------- */
  const [addresses, setAddresses] = useState([]);
  const [defaultAddressId, setDefaultAddressId] = useState(null);

  const [defaultIndex, setDefaultIndex] = useState(0);

  const [profileImage, setProfileImage] = useState(nouser);
  const toast = useRef(null); // optional if using toast


  /* ---------------- Notification ---------------- */
  const [subscribed, setSubscribed] = useState(true);

  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null); // null = add

  /* ---------------- Fetch Profile ---------------- */
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get(`/customerProfileRoutes/customer_profile_data`);

        if (res.data.success) {
          const customer = res.data.data;

          setFirstName(customer.C_fname);
          setLastName(customer.C_lname);
          setEmail(customer.C_email);
          setSubscribed(customer.is_subscribed);
          // ✅ SET PROFILE IMAGE
          setProfileImage(customer.image_url || nouser);
          if (customer.details?.length) {
            setAddresses(customer.details);

            const defaultAddr = customer.details.find(d => d.is_default);
            setDefaultAddressId(defaultAddr?.customer_details_id || null);
          }
          else {
            setAddresses([]);
          }

        }
      } catch (err) {
        console.error("Profile fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  /* ---------------- Address Handlers ---------------- */
  const openAddAddress = () => {
    setEditingAddress(null);
    setSidebarVisible(true);
  };

  const openEditAddress = (address) => {
    setEditingAddress(address);
    setSidebarVisible(true);
  };


  const saveAddress = async () => {
    if (!editingAddress) return;

    try {
      setLoading(true);

      const payload = {
        customer_details_id: editingAddress.customer_details_id || undefined,
        phone_no: editingAddress.phone_no,
        address_type: editingAddress.address_type || "shipping",
        is_default: editingAddress.is_default,
        address1: editingAddress.address1,
        address2: editingAddress.address2,
        city: editingAddress.city,
        state: editingAddress.state,
        country: editingAddress.country || "India",
        zipcode: editingAddress.zipcode,
      };

      const { data } = await api.post(
        `/customerProfileRoutes/customer/address_update`,
        payload,
      );

      if (!data.success) return;

      const savedAddress = data.data;

      setAddresses(prev => {
        // 🔁 EDIT
        if (editingAddress.customer_details_id) {
          return prev.map(addr =>
            addr.customer_details_id === savedAddress.customer_details_id
              ? savedAddress
              : savedAddress.is_default
                ? { ...addr, is_default: false }
                : addr
          );
        }

        // ➕ ADD
        return savedAddress.is_default
          ? [
            ...prev.map(addr => ({ ...addr, is_default: false })),
            savedAddress
          ]
          : [...prev, savedAddress];
      });

      // ✅ Track default address ID
      if (savedAddress.is_default) {
        setDefaultAddressId(savedAddress.customer_details_id);
      }

      setSidebarVisible(false);
      setEditingAddress(null);
    } catch (error) {
      console.error("Save address error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Toggle Subscription ---------------- */
  const toggleSubscription = async (value) => {
    try {
      // optimistic UI update
      setSubscribed(value);

      const res = await api.post(
        `/customerProfileRoutes/customer/toggle-subscription`
      );

      if (!res.data.success) {
        // rollback if API fails
        setSubscribed(prev => !prev);
      }
    } catch (error) {
      console.error("Subscription toggle failed:", error);
      // rollback on error
      setSubscribed(prev => !prev);
    }
  };

  /* ---------------- Change Default Address ---------------- */
  const changeDefaultAddress = async (addressId) => {
    try {
      // Optimistic UI
      setDefaultAddressId(addressId);

      const { data } = await api.post(
        `/customerProfileRoutes/customer/set-default-address`,
        { customer_details_id: addressId }
      );

      if (!data.success) {
        throw new Error("Failed to set default address");
      }

      // Sync addresses state
      setAddresses(prev =>
        prev.map(addr => ({
          ...addr,
          is_default: addr.customer_details_id === addressId,
        }))
      );

    } catch (error) {
      console.error("Default address change failed:", error);
    }
  };


  const uploadAvatar = async ({ files }) => {
    try {
      const file = files[0];

      const formData = new FormData();
      formData.append("avatar", file);

      const res = await api.post(
        "/customerProfileRoutes/customer/update_avatar",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        // ✅ update UI instantly
        setProfileImage(res.data.data.image_url);
      }
    } catch (err) {
      console.error("Upload failed:", err);
    }
  };



  const ProfileSkeleton = () => {
    return (
      <Row className="mx-0 user-dash">
        {/* Title */}
        <Col xs={12} className="mb-4">
          <Skeleton width="200px" height="2rem" />
        </Col>

        {/* Avatar */}
        <Col xs={12} className="mb-4">
          <Skeleton shape="circle" size="100px" />
        </Col>

        {/* Left Column */}
        <Col md={6} className="mb-4">
          <Skeleton width="60%" height="1.8rem" className="mb-3" />
          <Skeleton height="2.5rem" className="mb-2" />
          <Skeleton height="2.5rem" className="mb-2" />
          <Skeleton height="2.5rem" className="mb-2" />
          <Skeleton height="2.5rem" />
        </Col>

        {/* Right Column */}
        <Col md={6} className="mb-4">
          <Skeleton width="60%" height="1.8rem" className="mb-3" />
          <Skeleton height="2.5rem" className="mb-2" />
          <Skeleton height="2.5rem" className="mb-2" />
          <Skeleton height="4rem" className="mb-2" />
          <Skeleton height="4rem" />
        </Col>

        {/* Notification */}
        <Col xs={12} className="mb-4">
          <Skeleton width="80%" height="1.5rem" />
        </Col>

        {/* Save Button */}
        <Col xs={12} className="text-end">
          <Skeleton width="180px" height="3rem" />
        </Col>
      </Row>
    );
  };

  if (loading) {
    return <ProfileSkeleton />;
  }



  return (
    <>
      <Row className="mx-0 user-dash">
        <Col md={12} lg={12} xs={12} className="prof_title mb-4">
          <h4>My Profile</h4>
          <div className="position-relative mt-4" style={{ width: "80px" }}>
            <img
              src={profileImage}
              alt="Profile"
              style={{ width: "80px", height: "80px", objectFit: "cover" }}
              className="rounded-circle"
            />
            <FileUpload
              mode="basic"
              name="avatar"
              accept="image/*"
              maxFileSize={2000000}
              customUpload
              uploadHandler={uploadAvatar}
              auto
              chooseLabel="Change"
              className="FlUpload text-light"
            />
          </div>
        </Col>
        <Col md={6} lg={6} xs={12} className="prof_content mb-4">
          {/* <h4>Account Information</h4> */}
          <div className="d-flex flex-column gap-2 mb-2">
            <label >User Name</label>
            <InputText value={`${firstName} ${lastName}`} disabled />
          </div>
          <div className="d-flex flex-column gap-2 mb-2">
            <label >Email</label>
            <InputText value={email} disabled />
          </div>
          <div className="d-flex flex-column gap-2 mb-2">
            <label >Phone No.</label>
            <InputText
              value={
                addresses.find(a => a.customer_details_id === defaultAddressId)
                  ?.phone_no || ""
              }
              disabled
            />

          </div>
        </Col>
        <Col md={6} lg={6} xs={12} className="prof_content mb-4">
          {/* <h4>Address Data</h4> */}
          {/* Label with Plus Button */}
          <div className="d-flex align-items-center justify-content-start gap-4">
            <label>
              Address <span className="text-danger">*</span>
            </label>
            <Button
              icon="pi pi-plus"
              text
              className="p-0"
              style={{ fontSize: "14px", width: "34px", color: '#FFADD8' }}
              onClick={openAddAddress}
              tooltip="Add Multiple Address"
              tooltipOptions={{ position: "top" }}
            />
          </div>

          {/* Address Fields */}
          {addresses.map((address) => (
            <div
              key={address.customer_details_id}
              className="d-flex gap-2 mt-2 position-relative"
            >
              <RadioButton
                value={address.customer_details_id}
                checked={defaultAddressId === address.customer_details_id}
                onChange={() => changeDefaultAddress(address.customer_details_id)}
              />



              <InputTextarea
                rows={2}
                className="w-100 h-auto"
                disabled
                value={`${address.address1}, ${address.address2 || ""}, ${address.city}, ${address.state}, ${address.country} - ${address.zipcode}`}
              />

              <Button
                icon="pi pi-pencil"
                className="respedit"
                text
                style={{ color: '#FFADD8' }}
                onClick={() => openEditAddress(address)}
              />
            </div>
          ))}

        </Col>
        <Col md={12} lg={12} xs={12} className="prof_content mb-4">
          {/* <h4>Notification</h4> */}
          <div className="noti_msg">

            <InputSwitch
              checked={subscribed}
              onChange={(e) => toggleSubscription(e.value)}
            />
            <p className="mb-0" style={{ lineHeight: '2' }}>
              I wish to recieve newsletters, promotions and news from Shaadimaterial Company
            </p>
          </div>
        </Col>
        <Col md={12} lg={12} xs={12} className="prof_content mb-4">
          <p className="mb-0 w-75">
            Basic information on data protection: shaadimaterial company collects your data too improve our services and, if given consent, will keep you updated on news and promotions of shaadimaterial company projects.
          </p>
        </Col>
        <Col md={12} lg={12} xs={12} className="text-end">
          {/* <Button
            label="Save Changes"
            className="py-2"
            severity="warning"
            style={{ fontSize: '20px' }}
          /> */}
        </Col>
      </Row>


      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={() => setSidebarVisible(false)}
        className="shortCartSide"
        style={{ width: '500px' }}
      >
        <h4 className="mb-4 text-white">{editingAddress ? "Edit Address" : "Add New Address"}</h4>

        <div className="d-flex flex-column gap-3 mt-3 user-dash">
          {/* Phone Number */}
          <div className="d-flex flex-column gap-1">
            <label>Phone Number</label>
            <InputText
              placeholder="Enter phone number"
              value={editingAddress?.phone_no || ""}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  phone_no: e.target.value
                }))
              }
            />
          </div>

          {/* Address Line 1 */}
          <div className="d-flex flex-column gap-1">
            <label>Address Line 1</label>
            <InputText
              placeholder="House no, Street name"
              value={editingAddress?.address1 || ""}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  address1: e.target.value
                }))
              }
            />
          </div>

          {/* Address Line 2 */}
          <div className="d-flex flex-column gap-1">
            <label>Address Line 2 (Optional)</label>
            <InputText
              placeholder="Apartment, landmark"
              value={editingAddress?.address2 || ""}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  address2: e.target.value
                }))
              }
            />
          </div>

          {/* City */}
          <div className="d-flex flex-column gap-1">
            <label>City</label>
            <InputText
              placeholder="City"
              value={editingAddress?.city || ""}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  city: e.target.value
                }))
              }
            />
          </div>

          {/* State */}
          <div className="d-flex flex-column gap-1">
            <label>State</label>
            <InputText
              placeholder="State"
              value={editingAddress?.state || ""}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  state: e.target.value
                }))
              }
            />
          </div>

          {/* Country */}
          <div className="d-flex flex-column gap-1">
            <label>Country</label>
            <InputText
              placeholder="Country"
              value={editingAddress?.country || "India"}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  country: e.target.value
                }))
              }
            />
          </div>

          {/* Zipcode */}
          <div className="d-flex flex-column gap-1">
            <label>Zipcode</label>
            <InputText
              placeholder="Postal code"
              value={editingAddress?.zipcode || ""}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  zipcode: e.target.value
                }))
              }
            />
          </div>

          {/* Default Checkbox */}
          <div className="d-flex align-items-center gap-2 mt-2">
            <Checkbox
              checked={editingAddress?.is_default || false}
              onChange={(e) =>
                setEditingAddress(prev => ({
                  ...prev,
                  is_default: e.checked
                }))
              }
            />
            <label>
              Set as default address
            </label>
          </div>

          {/* Save Button */}
          <div className="d-flex justify-content-end mt-4">
            <Button
              label="Save Address"
              className="py-2 pink_button"
              style={{ width: 'fit-content' }}
              onClick={saveAddress}
              disabled={loading}
            />
          </div>
        </div>
      </Sidebar>


    </>
  );
};

export default Profile;